import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { firstValueFrom } from 'rxjs';

import { AuthService } from '@auth/services/auth.service';
import {
  establishmentJoinPath,
  establishmentLeavePath,
  OrderEventTypes,
  ProducerUserEventType,
  SignalREventTypes
} from '@core/models/signal-r';
import { LogService } from '@core/services/log.service';
import { ProducerIdentityService } from '@core/services/producer-identity.service';
import { environment } from '@environment/environment';

import { ApiService, RequestMethod } from '../../api.service';

@Injectable({
  providedIn: 'root'
})
export class EstablishmentHubSignalRService {
  private readonly apiService = inject(ApiService);
  private readonly logService = inject(LogService);
  private readonly authService = inject(AuthService);
  private readonly producerIdentityService = inject(ProducerIdentityService);

  private readonly NEGOTIATE_URL = `${environment.apiUrl}${environment.apis.notificationsEngine}/producer`;

  private hubConnection: HubConnection | null = null;
  private readonly connectionStateState = signal<HubConnectionState>(HubConnectionState.Disconnected);
  private currentEstablishmentGuid: string | null = null;

  // F11 (2026-10-30 review): serializes connect()/disconnect() against each other and against themselves, so an
  // app-wide sign-in effect and a page's own connect() (orders.page.ts) can never race into starting two
  // connections or stopping one mid-start. Each call chains onto whatever the previous one is doing rather than
  // running concurrently with it.
  private transition: Promise<void> = Promise.resolve();

  readonly connectionState = this.connectionStateState.asReadonly();
  readonly isConnected = computed(() => this.connectionStateState() === HubConnectionState.Connected);

  private readonly orderRefreshNeededState = signal(0);
  readonly orderRefreshNeeded = this.orderRefreshNeededState.asReadonly();

  constructor() {
    // MLP-1521: hold the producer-user-keyed connection open app-wide while signed in, so a user-targeted
    // claims-refresh reaches the producer on any page — not only while the Orders page has opened it. The
    // establishment group join stays page-scoped; this only governs the connection's lifetime.
    //
    // F11 (2026-10-30 review): connect only after the producer-user self grant is on the token, not merely on
    // isAuthenticated() — NE's negotiate requires that grant, and a brand-new sign-in's token carries none until
    // the facet bootstrap + re-mint (ProducerIdentityService) finishes, so connecting on isAuthenticated() alone
    // raced a first login into a 403.
    effect(() => {
      const authenticated = this.authService.isAuthenticated();
      untracked(() => {
        if (authenticated) {
          this.producerIdentityService
            .ensureFacetBootstrapped()
            .then(() => this.connect())
            .catch((error) =>
              this.logService.error('[EstablishmentHubSignalRService] auto-connect on sign-in failed', error)
            );
        } else {
          void this.disconnect();
        }
      });
    });
  }

  // F11: chains onto whatever connect()/disconnect() is already running rather than running concurrently with
  // it, so an app-wide sign-in effect and a page's own connect() call (orders.page.ts) can't race.
  private serialize(run: () => Promise<void>): Promise<void> {
    const settled = this.transition.then(run, run);
    this.transition = settled.then(
      () => undefined,
      () => undefined
    );
    return settled;
  }

  //connection point to establishment hub to listen for events.
  async connect(): Promise<void> {
    return this.serialize(() => this.connectOnce());
  }

  private async connectOnce(): Promise<void> {
    if (this.hubConnection) return;

    try {
      this.logService.info('[EstablishmentHubSignalRService] attempting to connect');
      const connection = new HubConnectionBuilder()
        .withUrl(this.NEGOTIATE_URL, {
          accessTokenFactory: () => this.getAuthToken(),
          transport: HttpTransportType.WebSockets,
          withCredentials: true
        })
        .withAutomaticReconnect()
        .build();
      this.hubConnection = connection;

      this.setupEventHandlers();
      this.setupConnectionStateHandlers();

      this.connectionStateState.set(HubConnectionState.Connecting);

      await connection.start();
      this.logService.info('[EstablishmentHubSignalRService] Connected successfully', {
        connectionId: connection.connectionId
      });
      this.connectionStateState.set(HubConnectionState.Connected);
    } catch (error) {
      this.logService.error('[EstablishmentHubSignalRService] Connection failed:', error);
      this.connectionStateState.set(HubConnectionState.Disconnected);
      // F11: null the failed attempt's connection, or connect()'s own guard above refuses every later retry —
      // after a first-login negotiate failure, the hub would otherwise never connect again without a reload.
      this.hubConnection = null;
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    return this.serialize(() => this.disconnectOnce());
  }

  private async disconnectOnce(): Promise<void> {
    const connection = this.hubConnection;
    if (!connection) return;

    // F11: cleared up front, not only on success — a stop that throws must not leave a dead connection object
    // standing in for a live one, or a logout → login in the same tab never reconnects without a reload.
    this.hubConnection = null;
    try {
      await connection.stop();
      this.logService.info('[EstablishmentHubSignalRService] Disconnected');
    } catch (error) {
      this.logService.error('[EstablishmentHubSignalRService] Error disconnecting:', error);
    } finally {
      this.connectionStateState.set(HubConnectionState.Disconnected);
    }
  }

  // helper to connect to specific establishment.
  async joinEstablishmentGroup(establishmentGuid: string): Promise<void> {
    if (!this.hubConnection || !this.isConnected()) {
      throw new Error('SignalR not connected');
    }

    try {
      const url = `${environment.apis.notificationsEngine}${establishmentJoinPath(establishmentGuid)}`;
      await firstValueFrom(
        this.apiService.request(
          RequestMethod.POST,
          url,
          'Error joining establishment group',
          {
            body: {
              connectionId: this.hubConnection.connectionId
            }
          },
          true
        )
      );

      this.currentEstablishmentGuid = establishmentGuid;
      this.logService.info(`[EstablishmentHubSignalRService] Joined Establishment Group: ${establishmentGuid}`);
    } catch (error) {
      this.logService.error('[EstablishmentHubSignalRService] Error Joining Establishment Group:', error);
      throw error;
    }
  }

  // not super useful not BUT when we get to multi establishments.
  // might be useful depending on how we implement swapping establishments (not current used)
  async leaveEstablishmentGroup(establishmentGuid: string): Promise<void> {
    if (!this.hubConnection || !this.isConnected()) {
      throw new Error('SignalR not connected');
    }

    try {
      const url = `${environment.apis.notificationsEngine}${establishmentLeavePath(establishmentGuid)}`;
      await firstValueFrom(
        this.apiService.request(
          RequestMethod.POST,
          url,
          'Error leaving establishment group',
          {
            body: {
              connectionId: this.hubConnection.connectionId
            }
          },
          true
        )
      );

      this.currentEstablishmentGuid = null;
      this.logService.info(`[EstablishmentHubSignalRService] Left Establishment Group: ${establishmentGuid}`);
    } catch (error) {
      this.logService.error('[EstablishmentHubSignalRService] Error Leaving Establishment Group:', error);
      throw error;
    }
  }

  // Gets the access token for authenticating with SignalR; the token is carried on connect.
  // Backend needs this to verify our user and if our user is able to access this establishment (TBD)
  private async getAuthToken(): Promise<string> {
    try {
      const token = await this.authService.getAuthToken();
      this.logService.info('[EstablishmentHubSignalRService] auth token retrieved');
      return token || '';
    } catch (error) {
      this.logService.error('[EstablishmentHubSignalRService] Error getting auth token:', error);
      throw error;
    }
  }

  // Set up listeners for incoming SignalR messages - where the magic happens
  private setupEventHandlers(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on(SignalREventTypes.EstablishmentGuidTargetedEventQueue, (messageName: any) => {
      this.logService.info(
        '[EstablishmentHubSignalRService] EstablishmentGuidTargetedEventQueue event received:',
        messageName
      );

      try {
        switch (messageName) {
          case OrderEventTypes.OrderPaid:
            this.orderRefreshNeededState.update((n) => n + 1);
            break;
          default:
            this.logService.debug('[EstablishmentHubSignalRService] Unhandled message type:', messageName);
            break;
        }
      } catch (error) {
        this.logService.error('[EstablishmentHubSignalRService] Error processing message:', error);
      }
    });

    // MLP-1521: user-targeted messages the producer hub keys on the producer-user id arrive on this same
    // connection. A ClaimsUpdated push means the producer's access changed, so the token they hold is stale.
    this.hubConnection.on(SignalREventTypes.UserEventMessage, (messageName: string) => {
      this.logService.info('[EstablishmentHubSignalRService] UserEventMessage received:', messageName);

      if (messageName === ProducerUserEventType.ClaimsUpdated) {
        this.authService
          .refreshClaims()
          .catch((error) => this.logService.warn('[EstablishmentHubSignalRService] refreshClaims failed', error));
      }
    });
  }

  // Handles connection lifecycle events like reconnecting, disconnected, etc. according to ask jeeves its helpful for debugging.
  private setupConnectionStateHandlers(): void {
    if (!this.hubConnection) return;

    this.hubConnection.onclose((error) => {
      this.logService.warn('[EstablishmentHubSignalRService] Connection closed', error);
      this.connectionStateState.set(HubConnectionState.Disconnected);
    });

    this.hubConnection.onreconnecting((error) => {
      this.logService.info('[EstablishmentHubSignalRService] Reconnecting...', error);
      this.connectionStateState.set(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected((connectionId) => {
      this.logService.info('[EstablishmentHubSignalRService] Reconnected:', connectionId);
      this.connectionStateState.set(HubConnectionState.Connected);

      // MLP-1521: a claims-refresh push may have been missed while disconnected; catch up by refreshing on reconnect.
      this.authService
        .refreshClaims()
        .catch((error) =>
          this.logService.warn('[EstablishmentHubSignalRService] refreshClaims on reconnect failed', error)
        );

      if (this.currentEstablishmentGuid) {
        this.joinEstablishmentGroup(this.currentEstablishmentGuid).catch((error) => {
          this.logService.error(
            '[EstablishmentHubSignalRService] Failed to rejoin establishment group after reconnect:',
            error
          );
        });
      }
    });
  }
}
